/**
 * Session Logging Settings Component
 * 
 * Allows coaches to configure session logging reminder settings for a specific team.
 * Includes controls for:
 * - Enabling/disabling reminders
 * - Setting send time (Eastern Time)
 * - Selecting specific athletes (or defaulting to all)
 * - Manually triggering reminders
 * 
 * @component
 */

import React, { useState, useEffect } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { getApiUrl } from '@/config/api';
import { authenticatedFetch } from '@/utils/apiUtils';

/** Component props */
interface SessionLoggingSettingsProps {
  /** Primary key of the team */
  teamId: number;
  /** Display name of the team */
  teamName: string;
}

/** Athlete type */
interface Athlete {
  id: number;
  full_name: string;
  email: string;
}

/** Settings type */
interface Settings {
  id: number;
  team: number;
  enabled: boolean;
  send_time: string;
  selected_athlete_ids: number[];
  target_athlete_count: number;
}

export const SessionLoggingSettings: React.FC<SessionLoggingSettingsProps> = ({
  teamId,
  teamName
}) => {
  const { isDark } = useTheme();
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  
  // Form state
  const [enabled, setEnabled] = useState(true);
  const [sendTime, setSendTime] = useState('20:00');
  const [selectedAthleteIds, setSelectedAthleteIds] = useState<number[]>([]);
  const [sendTo, setSendTo] = useState<'selected' | 'all'>('selected');
  const [triggering, setTriggering] = useState(false);

  /**
   * Fetch settings and athletes on component mount
   */
  useEffect(() => {
    fetchSettings();
    fetchAthletes();
  }, [teamId]);

  /**
   * Fetch session logging settings for the team.
   * Creates default settings if none exist.
   */
  const fetchSettings = async () => {
    try {
      const result = await authenticatedFetch(
        getApiUrl(`/api/teams/session-logging-settings/?team=${teamId}`),
        { method: 'GET' },
        'fetching session logging settings'
      );

      if (result.success && result.data) {
        if (result.data.results && result.data.results.length > 0) {
          const teamSettings = result.data.results[0];
          setSettings(teamSettings);
          setEnabled(teamSettings.enabled);
          setSendTime(teamSettings.send_time);
          setSelectedAthleteIds(teamSettings.selected_athlete_ids || []);
        }
      } else {
        console.error('Error fetching settings:', result.error);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch athletes for the team to populate the selector.
   */
  const fetchAthletes = async () => {
    try {
      const result = await authenticatedFetch(
        getApiUrl(`/api/teams/teams/${teamId}/`),
        { method: 'GET' },
        'fetching team athletes'
      );

      if (result.success && result.data) {
        let teamData = result.data;
        
        // Handle custom pagination format
        if (result.data.data) {
          teamData = result.data.data;
        }
        
        // API returns athletes array directly
        if (teamData.athletes && Array.isArray(teamData.athletes)) {
          setAthletes(teamData.athletes);
        }
        // Fallback: handle old member-based format
        else if (teamData.members && Array.isArray(teamData.members)) {
          const athleteMembers = teamData.members.filter(
            (member: any) => member.role === 'athlete'
          );
          setAthletes(athleteMembers.map((m: any) => m.user));
        }
        // Final fallback
        else {
          console.warn('Unexpected team data format:', teamData);
          setAthletes([]);
        }
      } else {
        console.error('Error fetching athletes:', result.error);
      }
    } catch (error) {
      console.error('Error fetching athletes:', error);
    }
  };

  /**
   * Save settings to backend API.
   * 
   * Creates new settings if none exist, otherwise updates existing settings.
   */
  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        team: teamId,
        enabled,
        send_time: sendTime,
        selected_athlete_ids: selectedAthleteIds,
      };

      const url = settings
        ? getApiUrl(`/api/teams/session-logging-settings/${settings.id}/`)
        : getApiUrl('/api/teams/session-logging-settings/');

      const method = settings ? 'PATCH' : 'POST';

      const result = await authenticatedFetch(
        url,
        {
          method,
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        },
        'saving session logging settings'
      );

      if (result.success && result.data) {
        setSettings(result.data);
        alert('Settings saved successfully!');
      } else {
        alert(`Error saving settings: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Manually trigger session logging texts.
   * 
   * Sends SMS reminders immediately based on the selected send_to option.
   */
  const handleManualTrigger = async () => {
    if (!settings) {
      alert('Please save settings first');
      return;
    }

    setTriggering(true);
    try {
      const result = await authenticatedFetch(
        getApiUrl(`/api/teams/session-logging-settings/${settings.id}/manual_trigger/`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ send_to: sendTo }),
        },
        'manually triggering session logging texts'
      );

      if (result.success && result.data) {
        alert(`Session logging texts sent to ${result.data.athlete_count} athletes!`);
      } else {
        alert(`Error: ${result.error || 'Failed to send texts'}`);
      }
    } catch (error) {
      console.error('Error triggering texts:', error);
      alert('Error triggering texts');
    } finally {
      setTriggering(false);
    }
  };

  /**
   * Toggle athlete selection.
   */
  const handleAthleteToggle = (athleteId: number) => {
    setSelectedAthleteIds(prev => {
      if (prev.includes(athleteId)) {
        return prev.filter(id => id !== athleteId);
      } else {
        return [...prev, athleteId];
      }
    });
  };

  if (loading) {
    return (
      <div className="py-4 cgm-text-secondary">
        Loading settings for {teamName}...
      </div>
    );
  }

  return (
    <div className="mb-6 p-6 cgm-card rounded-lg">
      <h3 className="text-xl font-bold mb-4 cgm-text-primary">{teamName}</h3>
      
      <div className="space-y-6">
        {/* Enable/Disable Toggle */}
        <div className="flex items-center justify-between">
          <div>
            <label className="text-sm font-medium cgm-text-primary">
              Enable Session Logging Reminders
            </label>
            <p className="text-sm cgm-text-secondary mt-1">
              Send automated SMS reminders to coaches and athletes
            </p>
          </div>
          <button
            type="button"
            onClick={() => setEnabled(!enabled)}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
              enabled ? 'bg-green-500' : isDark ? 'bg-gray-600' : 'bg-gray-300'
            }`}
            aria-label="Toggle session logging reminders"
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                enabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        {/* Time Picker */}
        <div>
          <label className="block text-sm font-medium cgm-text-primary mb-2">
            Send Time (Eastern Time)
          </label>
          <input
            type="time"
            value={sendTime}
            onChange={(e) => setSendTime(e.target.value)}
            className="cgm-input mt-1 block w-full px-3 py-2"
          />
          <p className="text-sm cgm-text-secondary mt-1">
            Daily reminders will be sent at this time in Eastern Time
          </p>
        </div>

        {/* Athlete Selector */}
        <div>
          <label className="block text-sm font-medium cgm-text-primary mb-2">
            Athletes to Receive Reminders
          </label>
          <div className="cgm-list-item rounded-md p-3 max-h-48 overflow-y-auto">
            {athletes.length === 0 ? (
              <p className="text-sm cgm-text-secondary">No athletes on this team</p>
            ) : (
              <div className="space-y-2">
                {athletes.map((athlete) => (
                  <label key={athlete.id} className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedAthleteIds.includes(athlete.id)}
                      onChange={() => handleAthleteToggle(athlete.id)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <span className="ml-2 text-sm cgm-text-primary">
                      {athlete.full_name || athlete.email}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <p className="text-sm cgm-text-secondary mt-1">
            Leave unchecked to send to all athletes ({athletes.length} total)
          </p>
        </div>

        {/* Save Button */}
        <button
          onClick={handleSave}
          disabled={saving}
          className={`cgm-button w-full px-4 py-2 rounded-xl transition-all ${
            saving ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>

        {/* Manual Trigger Section */}
        <div className="border-t border-[var(--card-border)] pt-6 mt-6">
          <h4 className="font-semibold cgm-text-primary mb-4">Manual Trigger</h4>
          
          <div className="space-y-2 mb-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                checked={sendTo === 'selected'}
                onChange={() => setSendTo('selected')}
                className="h-4 w-4 border-gray-300"
              />
              <span className="ml-2 text-sm cgm-text-primary">
                Send to selected athletes ({selectedAthleteIds.length || 'all'} athletes)
              </span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                checked={sendTo === 'all'}
                onChange={() => setSendTo('all')}
                className="h-4 w-4 border-gray-300"
              />
              <span className="ml-2 text-sm cgm-text-primary">
                Send to all athletes ({athletes.length} athletes)
              </span>
            </label>
          </div>
          
          <button
            onClick={handleManualTrigger}
            disabled={triggering || !settings}
            className={`cgm-button w-full px-4 py-2 rounded-xl transition-all ${
              triggering || !settings ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {triggering ? 'Sending...' : 'Send Texts Now'}
          </button>
          
          {!settings && (
            <p className="text-sm cgm-text-secondary mt-2">
              Save settings first before manually triggering texts
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionLoggingSettings;
